// src/screens/ProfileBanners/ProfileBannerScreen.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import './ProfileBannerScreen.css';

import backIcon from '../../assets/backspace-icon.png';
import placeholderProfile from '../../assets/placeholder-profile.png';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProfileBannerDrawer from '../../components/ProfileBannerDrawer';

import {
  getCatalogue,
} from '../../services/profileBannerApi';


import {
  trackBannerScreenView,
} from '../../services/revenueAnalytics';


const getBannerId = (banner) => {
  return banner?._id || banner?.id || banner?.bannerId || '';
};

const getCollectionName = (banner) => {
  const value = String(
    banner?.collectionKey ||
      banner?.collection ||
      'other'
  ).trim();

  if (!value) {
    return 'Other';
  }

  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1).toLowerCase()
    )
    .join(' ');
};

const getCatalogueItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.banners)) return data.banners;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.catalogue)) return data.catalogue;

  return [];
};

const getTrialUsed = (data) => {
  return Boolean(
    data?.trial?.used ??
    data?.trialUsed ??
    data?.profileBannerTrialUsed ??
    data?.hasUsedTrial
  );
};


function ProfileBannerScreen({ user, onUserUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [banners, setBanners] = useState([]);
  const [selectedBanner, setSelectedBanner] = useState(null);

  const [trialUsed, setTrialUsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadCatalogue = useCallback(async ({ background = false } = {}) => {
    if (background) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      setError('');

      const data = await getCatalogue();
      const items = getCatalogueItems(data);

      setBanners(items);
      setTrialUsed(getTrialUsed(data));

      setSelectedBanner((current) => {
        if (!current) return null;

        const currentId = String(getBannerId(current));

        return (
          items.find(
            (banner) => String(getBannerId(banner)) === currentId
          ) || current
        );
      });
    } catch (err) {
      console.error('Failed to load profile banners:', err);

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Could not load profile banners. Please try again.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadCatalogue();
  }, [loadCatalogue]);


useEffect(() => {
  void trackBannerScreenView();
}, []);


  const groupedCollections = useMemo(() => {
    const groups = new Map();

    banners.forEach((banner) => {
      const collection = getCollectionName(banner);

      if (!groups.has(collection)) {
        groups.set(collection, []);
      }

      groups.get(collection).push(banner);
    });

    return Array.from(groups.entries()).map(([name, items]) => ({
      name,
      items: [...items].sort(
        (a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
      ),
    }));
  }, [banners]);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, { replace: true });
      return;
    }

    navigate(-1);
  };

  const handleOpenBanner = (banner) => {
    setSelectedBanner(banner);
  };

  const handleCloseDrawer = () => {
    setSelectedBanner(null);
  };

  const handleBannerStateChange = async ({
    equippedBanner,
    unequipped = false,
  } = {}) => {
    if (typeof onUserUpdate === 'function') {
      const nextUser = {
        ...(user || {}),
        equippedProfileBanner: unequipped
          ? null
          : equippedBanner || user?.equippedProfileBanner || null,
      };

      onUserUpdate(nextUser);
    }

    try {
      const viewerUsername = String(user?.username || '')
        .trim()
        .toLowerCase();

      if (viewerUsername) {
        const ownProfileCacheKey =
          `viewedUserProfile:${viewerUsername}:${viewerUsername}`;

        localStorage.removeItem(ownProfileCacheKey);
      }
    } catch (_) {}

    await loadCatalogue({ background: true });
  };

  const getStatus = (banner) => {
    if (banner?.equipped) {
      return {
        label: 'Equipped',
        className: 'is-equipped',
      };
    }

    if (banner?.owned || banner?.accessType === 'purchased') {
      return {
        label: 'Owned',
        className: 'is-owned',
      };
    }

    if (banner?.trialActive) {
      return {
        label: 'Trial Active',
        className: 'is-trial',
      };
    }

    if (banner?.trialExpired) {
      return {
        label: 'Trial Expired',
        className: 'is-expired',
      };
    }

    return null;
  };

  return (
    <div className="profile-banners-screen">
      <header className="profile-banners-topbar">
        <button
          type="button"
          className="profile-banners-back-btn"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={backIcon} alt="" />
        </button>

        <h1>Profile Banners</h1>

        <div className="profile-banners-topbar-space" />
      </header>

      <main className="profile-banners-scroll">
        <section className="profile-banners-intro">
          <div className="profile-banners-intro-icon">
            <img
              src={user?.profilePic || placeholderProfile}
              alt=""
            />
          </div>

          <div className="profile-banners-intro-copy">
            <h2>Make your profile yours</h2>

            <p>
              Purchase a banner once and keep it permanently. You can
              equip, switch, or remove owned banners at any time.
            </p>
          </div>
        </section>

{!trialUsed && (
  <section className="profile-banners-trial-note">
    <strong>Try one banner free for 7 days</strong>

    <span>
      Choose carefully. Each account receives only one banner trial.
    </span>
  </section>
)}

        {loading ? (
          <div className="profile-banners-loading">
            <LoadingSpinner size="medium" />
          </div>
        ) : error ? (
          <div className="profile-banners-error">
            <p>{error}</p>

            <button
              type="button"
              onClick={() => loadCatalogue()}
            >
              Try Again
            </button>
          </div>
        ) : groupedCollections.length === 0 ? (
          <div className="profile-banners-empty">
            <h3>No banners available</h3>
            <p>New banner collections will appear here.</p>
          </div>
        ) : (
          <div className="profile-banners-collections">
            {groupedCollections.map((collection) => (
              <section
                key={collection.name}
                className="profile-banners-collection"
              >
                <div className="profile-banners-section-heading">
                  <div>
                    <h2>{collection.name}</h2>
                    <p>
                      {collection.items.length}{' '}
                      {collection.items.length === 1
                        ? 'banner'
                        : 'banners'}
                    </p>
                  </div>
                </div>

                <div className="profile-banners-grid">
                  {collection.items.map((banner) => {
                    const bannerId = getBannerId(banner);
                    const status = getStatus(banner);

                    return (
                      <button
                        key={bannerId}
                        type="button"
                        className="profile-banner-card"
                        onClick={() => handleOpenBanner(banner)}
                      >
                        <div className="profile-banner-card-preview">
                          <img
                            src={banner.imageUrl}
                            alt={banner.name || 'Profile banner'}
                            loading="lazy"
                          />

                          {status && (
                            <span
                              className={`profile-banner-card-status ${status.className}`}
                            >
                              {status.label}
                            </span>
                          )}

                          <span className="profile-banner-card-price">
                            {Number(banner?.pricePi || 5)} Pi
                          </span>
                        </div>

                        <div className="profile-banner-card-info">
                          <strong>
                            {banner.name || 'Profile Banner'}
                          </strong>

                          <span>Tap to preview</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {refreshing && (
          <div className="profile-banners-refreshing">
            Updating banners…
          </div>
        )}
      </main>

<ProfileBannerDrawer
  isOpen={Boolean(selectedBanner)}
  banner={selectedBanner}
  banners={banners}
  user={user}
  trialUsed={trialUsed}
  onSelectBanner={setSelectedBanner}
  onClose={handleCloseDrawer}
  onBannerStateChange={handleBannerStateChange}
/>

    </div>
  );
}

export default ProfileBannerScreen;
